/**
 *  @class
 *  @function FreeShipping
 */

if (!customElements.get("free-shipping")) {
  // Parse the threshold value out of the Global-E free-shipping banner injected
  // at the top of the page (e.g. "Free shipping to United States on all orders
  // above $250.00"). Returns the value in cents (matching cart.total_price), or
  // null if no banner is present or the amount can't be read.
  function readGeThreshold() {
    const el =
      document.getElementById("FreeShippingBanner") ||
      document.querySelector(
        ".ge-free-shipping-msg-desktop, .ge-free-shipping-msg-mobile, .ge-free-shipping-msg"
      )
    if (!el) return null

    const match = (el.textContent || "").match(/[\d][\d.,\s]*\d|\d+/g)
    if (!match || !match.length) return null

    // Currency symbols may sit in the middle of the text; the threshold is the
    // last numeric run.
    const raw = match[match.length - 1].replace(/\s/g, "")
    const lastDot = raw.lastIndexOf(".")
    const lastComma = raw.lastIndexOf(",")

    // Locale-aware separator handling. Two separator types present → the later
    // one is the decimal mark, the other is thousands grouping. One separator
    // type with exactly three trailing digits → thousands grouping (e.g. KRW
    // "200,000", EUR "1.234"). Otherwise → decimal mark.
    let normalised
    if (lastDot === -1 && lastComma === -1) {
      normalised = raw
    } else if (lastDot !== -1 && lastComma !== -1) {
      normalised =
        lastDot > lastComma
          ? raw.replace(/,/g, "")
          : raw.replace(/\./g, "").replace(",", ".")
    } else {
      const sep = lastDot !== -1 ? "." : ","
      const lastSep = lastDot !== -1 ? lastDot : lastComma
      const trailingDigits = raw.length - lastSep - 1
      const onlyOneSep = raw.indexOf(sep) === lastSep
      if (onlyOneSep && trailingDigits === 3) {
        // Single separator, three digits after → thousands grouping, no decimal
        normalised = raw.split(sep).join("")
      } else if (sep === ".") {
        normalised = raw.replace(/,/g, "")
      } else {
        normalised = raw.replace(/\./g, "").replace(",", ".")
      }
    }

    const value = parseFloat(normalised)
    if (!isFinite(value) || value <= 0) return null

    return Math.round(value * 100)
  }

  class FreeShipping extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.amountText = this.querySelector(".free-shipping--text span")
      this.remainingText = this.querySelector(".free-shipping--text-remaining")
      this.fullText = this.querySelector(".free-shipping--text-full")

      this.render()

      // The GE banner is injected asynchronously on GE-managed markets. If it
      // wasn't in the DOM when we first rendered, watch for it and re-render
      // once it appears so we use the correct per-market threshold. AU markets
      // never receive a GE banner, so a 2s fallback disconnects the observer
      // rather than leaving it watching document.body indefinitely.
      if (readGeThreshold() == null) {
        this.observer = new MutationObserver(() => {
          if (readGeThreshold() != null) {
            this.stopWatchingForGe()
            this.render()
          }
        })
        this.observer.observe(document.body, { childList: true, subtree: true })
        this.geWatchTimeout = setTimeout(() => this.stopWatchingForGe(), 2000)
      }
    }

    disconnectedCallback() {
      this.stopWatchingForGe()
    }

    stopWatchingForGe() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      if (this.geWatchTimeout) {
        clearTimeout(this.geWatchTimeout)
        this.geWatchTimeout = null
      }
    }

    render() {
      const total = parseInt(this.dataset.cartTotal, 10)

      // Prefer the Global-E banner's per-market threshold (already in display
      // currency). Fall back to the AU theme setting × Shopify FX rate when GE
      // isn't active (e.g. AU customers).
      const geMinimum = readGeThreshold()
      const minimum =
        geMinimum != null
          ? geMinimum
          : Math.round(
              parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1)
            )

      let percentage = 1

      if (total < minimum) {
        percentage = total / minimum

        if (this.amountText) {
          const remaining = minimum - total
          const format =
            window.theme.settings.money_with_currency_format || "${{amount}}"
          this.amountText.innerHTML = formatMoney(remaining, format)
        }

        if (this.remainingText && this.fullText) {
          this.remainingText.style.display = "block"
          this.fullText.style.display = "none"
        }
      } else {
        if (this.remainingText && this.fullText) {
          this.remainingText.style.display = "none"
          this.fullText.style.display = "block"
        }
      }

      this.style.setProperty("--percentage", percentage)
    }
  }

  customElements.define("free-shipping", FreeShipping)
}

